import React, { useEffect, useState } from 'react';
import { Responsive, WidthProvider, Layouts } from 'react-grid-layout';
import Loader from '../common/Loader/Loader';
import HeaderElements from '../navigation/components/HeaderElements';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import OpportunitiesChart from '../recommendations/Opportunities/OpportunitiesChart';
import WellSummaryWidget from './components/WellSummaryWidget';
import './style.scss';
import OptimizationSummary from './components/OptimizationSummary';
import {
  fetchGLDashboard,
  selectGLChartData,
  selectGLHighDaysSinceTested,
  selectGLOptimizationData,
  selectGLParetoCounts,
  selectGLSummaryCount,
} from './glDashboardSlice';
import ParetoProduction from './components/ParetoProduction';
import { GLDashboardWidget } from '../../types/GLDashboardTypes';
import SelectedWellFilters from '../well-filters/SelectedWellFilters';
import { handleRemoveFilter } from '../well-filters/wellFilterUtils';
import MenuNavIcon from '../navigation/components/NavMenuIcon';

const ResponsiveGridLayout = WidthProvider(Responsive);
const layouts: Layouts = {
  lg: [
    { i: GLDashboardWidget.topRowWidget, x: 0, y: 0, w: 2, h: 4 },
    { i: GLDashboardWidget.optimization, x: 0, y: 4, w: 1, h: 13 },
    { i: GLDashboardWidget.pareto, x: 1, y: 4, w: 1, h: 13 },
    { i: GLDashboardWidget.opportunities, x: 0, y: 8, w: 2, h: 16 },
  ],
  md: [
    { i: GLDashboardWidget.topRowWidget, x: 0, y: 0, w: 2, h: 4 },
    { i: GLDashboardWidget.optimization, x: 0, y: 6, w: 6, h: 9 },
    { i: GLDashboardWidget.pareto, x: 0, y: 10, w: 6, h: 14 },
    { i: GLDashboardWidget.opportunities, x: 0, y: 14, w: 6, h: 14 },
  ],
  sm: [
    { i: GLDashboardWidget.topRowWidget, x: 0, y: 0, w: 1, h: 8 },
    { i: GLDashboardWidget.optimization, x: 0, y: 8, w: 1, h: 9 },
    { i: GLDashboardWidget.pareto, x: 0, y: 12, w: 1, h: 23 },
    { i: GLDashboardWidget.opportunities, x: 0, y: 16, w: 1, h: 10 },
  ],
};

const GLDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useAppDispatch();

  // server slice selectors
  const summaryCounts = useAppSelector(selectGLSummaryCount);
  const paretoCounts = useAppSelector(selectGLParetoCounts);
  const chartData = useAppSelector(selectGLChartData);
  const highDaysSinceTested = useAppSelector(selectGLHighDaysSinceTested);
  const optimizationData = useAppSelector(selectGLOptimizationData);

  // well-filters slice
  const selectedFilters = useAppSelector((state: any) => state.WellFiltersSlice.PCSelectedFilters ?? []);
  const pcWellIds = useAppSelector((state) => state.WellFiltersSlice.PCWellIds);

  const getFilterIds = (section: string) =>
    selectedFilters
      .filter((f: any) => f.section === section && typeof f.id === 'number')
      .map((f: any) => f.id)
      .join(',');

  useEffect(() => {
    const load = async () => {
      try {
        const areaIds = getFilterIds('Area');
        const businessUnitIds = getFilterIds('Business unit');
        const fieldIds = getFilterIds('Field');

        const params: any = {};
        if (areaIds !== '') params.areaIds = areaIds;
        if (businessUnitIds !== '') params.businessUnitIds = businessUnitIds;
        if (fieldIds !== '') params.fieldIds = fieldIds;

        await dispatch(
          fetchGLDashboard({
            areaIds: areaIds !== '' ? areaIds : undefined,
            businessUnitIds: businessUnitIds !== '' ? businessUnitIds : undefined,
            fieldIds: fieldIds !== '' ? fieldIds : undefined,
            wellIds: pcWellIds || undefined,
          }),
        ).unwrap();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch GL dashboard/opportunities:', err);
        setErrorMessage('Failed to load GL dashboard');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [dispatch, selectedFilters, pcWellIds]);

  if (isLoading) {
    return <Loader />;
  }

  const hasSelectedFilters = Array.isArray(selectedFilters) && selectedFilters.length > 0;

  const handleRemoveFilterUtil = (filterToRemove: any) => {
    handleRemoveFilter(filterToRemove, selectedFilters, dispatch);
  };

  return (
    <div className={`gl-dashboard ${hasSelectedFilters ? 'has-selected-filters' : ''}`}>
      {errorMessage && (
        <div className='error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
          {errorMessage}
        </div>
      )}

      <div className='summary-header-container'>
        <div className='group-status-top-row'>
          <div className='nav-menu-icon-header-container'>
            <MenuNavIcon />
            <span className='group-status-group-name'>Gaslift dashboard</span>
          </div>
          <div className='custom-views-header-elements'>
            <HeaderElements showFilterIcon={true} />
          </div>
        </div>
      </div>
      <div className='summary-header-filters-section'>
        <SelectedWellFilters filters={selectedFilters} onRemoveFilter={handleRemoveFilterUtil} />
      </div>
      <div className='gl-dashboard-content'>
        <ResponsiveGridLayout
          className='layout'
          layouts={layouts}
          breakpoints={{ lg: 1100, md: 800, sm: 686 }}
          cols={{ lg: 2, md: 2, sm: 1 }}
          rowHeight={25}
          isResizable={false}
          isDraggable={false}
        >
          <div key={GLDashboardWidget.topRowWidget}>
            <WellSummaryWidget count={summaryCounts} HighDaysSinceTested={highDaysSinceTested} />
          </div>
          <div key={GLDashboardWidget.optimization}>
            <OptimizationSummary data={optimizationData} />
          </div>
          <div key={GLDashboardWidget.pareto}>
            <ParetoProduction count={paretoCounts} chartData={chartData} />
          </div>
          <div key={GLDashboardWidget.opportunities}>
            <OpportunitiesChart
              header='Optimization opportunities'
              showTabs={false}
              parentActiveTab='gl'
              fullScreen={true}
            />
          </div>
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

export default GLDashboard;
